import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { liveChallengeActivationsApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { LiveChallengeResults } from '@/components/custom/live-challenge-results';

const formatDateTime = value => (value ? new Date(value).toLocaleString('pt-BR') : '—');
const formatPercentage = value => (value === null || value === undefined ? null : `${Number(value).toString().replace('.', ',')}%`);

export default function FinalView({ activationId, challenge }) {
  const navigate = useNavigate();
  const client = useClient();
  const playfulTeacher = usePlayfulTeacher();
  const { leaderboard: contextLeaderboard } = useLiveChallenge();
  const [ranking, setRanking] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;

    // Mesmo endpoint usado na modal de Classificação do relatório de Desafio Ao
    // Vivo (pages/reports/LiveChallenge/ClassificationModal.jsx) — a API
    // operacional de leaderboard não retorna instituição/turma/e-mail do
    // participante, só esse endpoint de relatório traz esses campos.
    client.fetch(`/reports/live_challenges/activations/${activationId}/ranking`).then(({ data, error }) => {
      if (!active) return;

      if (error) {
        // Fallback: mantém o painel utilizável (sem instituição/e-mail) se o
        // endpoint de relatório falhar (ex.: usuário sem a permissão "Visualizar
        // Relatório de Desafio Ao Vivo").
        liveChallengeActivationsApi
          .leaderboard(activationId, 100)
          .then(fallback => {
            if (!active) return;
            const items = Array.isArray(fallback?.data) ? fallback.data : [];
            setRanking(
              items.map((it, index) => {
                const a = it.attributes || {};
                return {
                  id: a.user?.id || it.id || index,
                  name: a.user?.name || `Participante ${index + 1}`,
                  email: null,
                  institutionCourse: null,
                  classroom: null,
                  points: a['total-score'] ?? a.total_score ?? 0
                };
              })
            );
          })
          .catch(() => {
            setRanking(
              (contextLeaderboard || []).map((entry, index) => ({
                id: entry.user_id || entry.id || index,
                name: entry.user_name || entry.name || `Participante ${index + 1}`,
                email: null,
                institutionCourse: null,
                classroom: null,
                points: entry.total_score ?? entry.score ?? 0
              }))
            );
          });
        setSummary(null);
        return;
      }

      setSummary(data);
      setRanking(
        (data.ranking || []).map(student => ({
          id: student['participant-id'],
          name: student.name,
          email: student.email,
          institutionCourse: student.institution,
          classroom: student.classroom,
          points: student.score
        }))
      );
    });

    return () => {
      active = false;
    };
  }, [activationId, client, contextLeaderboard]);

  const filteredRanking = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ranking;
    return ranking.filter(student => student.name.toLowerCase().includes(term) || student.email?.toLowerCase().includes(term));
  }, [ranking, search]);

  const statCards = [
    { primary: challenge?.title || 'Desafio ao vivo', secondary: 'Desafio' },
    { primary: formatDateTime(challenge?.started_at || challenge?.created_at), secondary: 'Ativação' },
    { primary: summary?.['participants-count'] ?? ranking.length, secondary: 'Participantes' },
    { primary: formatPercentage(summary?.['accuracy-percentage']) ?? '-', secondary: 'De acerto' }
  ];

  return (
    <div className="flex flex-col gap-6 py-4">
      <LiveChallengeResults
        playfulTeacher={playfulTeacher}
        statCards={statCards}
        search={search}
        onSearchChange={setSearch}
        ranking={filteredRanking}
        emptyText="Nenhum participante registrou pontos."
      />

      <div className="mx-auto w-full max-w-md">
        {playfulTeacher ? (
          <PlayfulCardButton
            className="h-12 text-base"
            onClick={() => navigate('/admin/desafio-ao-vivo')}
          >
            Encerrar painel
          </PlayfulCardButton>
        ) : (
          <Button
            type="button"
            variant="dark"
            size="lg"
            fullWidth
            onClick={() => navigate('/admin/desafio-ao-vivo')}
          >
            Encerrar painel
          </Button>
        )}
      </div>
    </div>
  );
}
